import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, Trophy, ArrowRight, RotateCcw, Volume2 } from 'lucide-react';
import confetti from 'canvas-confetti';
import { sounds } from '../../utils/SoundManager';

const Quiz = ({ questions, onComplete }) => {
    const [currentIdx, setCurrentIdx] = useState(0);
    const [score, setScore] = useState(0);
    const [showResult, setShowResult] = useState(false);
    const [feedback, setFeedback] = useState(null); // 'correct' | 'incorrect'
    const [selectedOption, setSelectedOption] = useState(null);

    const currentQ = questions[currentIdx];

    const handleOptionClick = (optionIdx) => {
        if (feedback) return; // Prevent double clicking

        setSelectedOption(optionIdx);
        const isCorrect = optionIdx === currentQ.correct;

        if (isCorrect) {
            setFeedback('correct');
            setScore(score + 1);
            sounds.playCorrect();
            confetti({
                particleCount: 30,
                spread: 50,
                origin: { y: 0.7 },
                colors: ['#70E000', '#4CC9F0']
            });
        } else {
            setFeedback('incorrect');
            sounds.playIncorrect();
        }
    };

    const nextQuestion = () => {
        setFeedback(null);
        setSelectedOption(null);

        if (currentIdx < questions.length - 1) {
            setCurrentIdx(currentIdx + 1);
        } else {
            setShowResult(true);
            const finalScore = score; // capture current score state
            if (questions.length === finalScore) {
                sounds.playCelebration();
                confetti({ particleCount: 150, spread: 100 });
            }
            if (onComplete) {
                onComplete(finalScore);
            }
        }
    };

    const restart = () => {
        setCurrentIdx(0);
        setScore(0);
        setShowResult(false);
        setFeedback(null);
        setSelectedOption(null);
    }

    if (showResult) {
        return (
            <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-white rounded-3xl p-8 shadow-2xl text-center border-4 border-brand-yellow space-y-6"
            >
                <div className="w-24 h-24 bg-brand-yellow rounded-full mx-auto flex items-center justify-center shadow-lg">
                    <Trophy size={48} className="text-white" />
                </div>
                <div>
                    <h2 className="text-3xl font-bold text-gray-800">¡Quiz Terminado!</h2>
                    <p className="text-xl text-gray-500 mt-2">Has acertado <span className="font-bold text-brand-purple text-2xl">{score}</span> de {questions.length}</p>
                </div>

                <div className="flex justify-center gap-4">
                    <button onClick={restart} className="flex items-center gap-2 px-6 py-3 bg-gray-100 rounded-xl font-bold text-gray-600 hover:bg-gray-200 transition-colors">
                        <RotateCcw size={20} /> Repetir
                    </button>
                </div>
            </motion.div>
        );
    }

    return (
        <div className="bg-white rounded-3xl p-6 md:p-10 shadow-xl border-b-8 border-brand-purple/20 relative overflow-hidden">
            {/* Progress Bar */}
            <div className="absolute top-0 left-0 h-2 bg-brand-purple transition-all duration-500" style={{ width: `${((currentIdx + 1) / questions.length) * 100}%` }} />

            <div className="mb-8">
                <span className="text-sm font-bold text-brand-purple uppercase tracking-widest bg-brand-purple/10 px-3 py-1 rounded-lg">
                    Pregunta {currentIdx + 1} de {questions.length}
                </span>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mt-4 leading-tight">
                    {currentQ.question}
                </h3>
            </div>

            {/* Visual Context (Optional) */}
            {currentQ.visual && (
                <div className="mb-8 flex justify-center">
                    <div className="w-full max-w-sm h-48 bg-gray-50 rounded-2xl border-2 border-gray-100 flex items-center justify-center p-4">
                        {currentQ.visual}
                    </div>
                </div>
            )}

            {/* Options */}
            <div className="grid gap-4">
                {currentQ.options.map((opt, idx) => {
                    let stateStyle = 'bg-gray-50 border-gray-200 hover:border-brand-blue hover:bg-blue-50';
                    let icon = null;

                    if (selectedOption === idx) {
                        if (feedback === 'correct') {
                            stateStyle = 'bg-green-100 border-green-500 text-green-800 ring-2 ring-green-200';
                            icon = <Check size={24} />;
                        } else if (feedback === 'incorrect') {
                            stateStyle = 'bg-red-100 border-red-500 text-red-800 ring-2 ring-red-200';
                            icon = <X size={24} />;
                        }
                    } else if (feedback && idx === currentQ.correct) {
                        // Highlight incorrect answer
                        stateStyle = 'bg-green-50 border-green-300 opacity-70';
                    } else if (feedback) {
                        stateStyle = 'opacity-50';
                    }

                    return (
                        <button
                            key={idx}
                            disabled={!!feedback}
                            onClick={() => handleOptionClick(idx)}
                            className={`
                        w-full text-left p-6 rounded-2xl border-2 transition-all flex items-center justify-between group
                        font-bold text-lg
                        ${stateStyle}
                    `}
                        >
                            <span>{opt}</span>
                            {icon}
                        </button>
                    )
                })}
            </div>

            {/* Next Button */}
            <AnimatePresence>
                {feedback && (
                    <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        className="mt-8 flex justify-end"
                    >
                        <button
                            onClick={nextQuestion}
                            className="bg-brand-purple text-white px-8 py-3 rounded-xl font-bold flex items-center gap-2 shadow-lg hover:bg-brand-purple/90 transform hover:scale-105 transition-all"
                        >
                            {currentIdx === questions.length - 1 ? 'Ver Resultado' : 'Siguiente'} <ArrowRight size={20} />
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

        </div>
    );
};

export default Quiz;
